// Endpoint pattern matching — a faithful client-side replica of the
// auth-service's patternMatchImpl + selectBestMatch (godev
// auth-service/policy-engine.go), for the endpoint-tester UI: type an
// endpoint + pick a group, see which rule wins. UI-free and unit-tested.
// FEATURE-20260716-sql-firewall-policy-editor

export type EndpointRule = {
  uid: string;
  endpoint_pattern: string;
  usergroup_uid: string | null;
  granted: boolean;
  priority: number;
  created_at?: string;
};

/**
 * (matched, specificity) — exact = 10000; "x." / "x.*" prefix = 100 + 50 per
 * segment; "*" catch-all = 1; else no match. Mirrors patternMatchImpl.
 */
export function patternMatch(endpoint: string, pattern: string): [boolean, number] {
  if (pattern === "*") return [true, 1];
  if (endpoint === pattern) return [true, 10000];
  if (pattern.endsWith(".*")) {
    const prefix = pattern.slice(0, -1); // trim the *
    if (endpoint.startsWith(prefix)) {
      const segments = prefix.replace(/\.$/, "").split(".");
      return [true, 100 + segments.length * 50];
    }
  }
  if (pattern.endsWith(".")) {
    if (endpoint.startsWith(pattern)) {
      const segments = pattern.replace(/\.$/, "").split(".");
      return [true, 100 + segments.length * 50];
    }
  }
  return [false, 0];
}

export type EndpointDecision = {
  winner?: EndpointRule & { specificity: number };
  /** every rule that matched, in decision order (specificity desc, priority desc, newer first) */
  matches: Array<EndpointRule & { specificity: number }>;
};

/**
 * Which rule decides `endpoint` for a caller in `groupUids` (their group set;
 * rules with usergroup_uid NULL apply to everyone). Mirrors selectBestMatch:
 * specificity desc, then priority desc, then created_at desc.
 */
export function decideEndpoint(
  endpoint: string,
  groupUids: Set<string>,
  rules: EndpointRule[],
): EndpointDecision {
  const matches = rules
    .filter((r) => r.usergroup_uid === null || groupUids.has(r.usergroup_uid))
    .map((r) => {
      const [ok, specificity] = patternMatch(endpoint, r.endpoint_pattern);
      return ok ? { ...r, specificity } : null;
    })
    .filter((r): r is EndpointRule & { specificity: number } => r !== null)
    .sort((a, b) => {
      if (a.specificity !== b.specificity) return b.specificity - a.specificity;
      if (a.priority !== b.priority) return b.priority - a.priority;
      return String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
    });
  return { winner: matches[0], matches };
}
